import { auth } from '../../lib/firebase';

/**
 * storage-4 (docs/111 Wave 1) — 写真 upload 前の地区 claim 自動更新。
 *
 * 背景:
 *   Storage 規則の写真 path は `canAccessDistrict(districtId)` を要求する (custom claim
 *   `accessibleDistricts` に districtId を含むか、role==='master')。一方 Firestore の posts create は
 *   `isOwner` のみ (claim 非依存) のため、claim が古い/欠落していると **写真付き投稿だけが
 *   Storage 規則で permission-denied になり丸ごと失敗** する (テキスト投稿は通る) という非対称が起きる。
 *
 *   地区 claim は `resolveDistrictByToken` (/m/:token 解決時) が付与するが、client の ID token は
 *   `useDistrictResolver` の 7 日 cache hit 時に `getIdToken(true)` を skip するため、
 *   「サーバには claim があるのに client token が古い」状態が発生する。
 *
 * 対処 (本ファイル):
 *   写真 upload 直前に現 token の claim を検証し、対象地区が無ければ `getIdToken(true)` で 1 度だけ
 *   強制 refresh して最新 claim を引き直す。これで「サーバ付与済だが token が古い」ケースを解消する。
 *   rules は変更しない (client 限定の修正)。
 */

/**
 * ID token の custom claims が対象 districtId へのアクセス権を持つか。
 * firestore.rules / storage.rules の `canAccessDistrict` と同セマンティクス:
 *   role==='master' か、accessibleDistricts (list) に districtId を含む。
 */
export function hasDistrictAccessClaim(
  claims: Record<string, unknown>,
  districtId: string,
): boolean {
  if (claims.role === 'master') return true;
  const list = claims.accessibleDistricts;
  return Array.isArray(list) && list.includes(districtId);
}

/**
 * 写真 upload 前に地区 claim を最新化する (best-effort)。
 * claim が既にあれば network を伴わず即 return。欠落時のみ `getIdToken(true)` で 1 度 refresh。
 * 取得/refresh 失敗・refresh 後もなお欠落のときは throw せず続行し、本来の upload エラーに委ねる。
 */
export async function ensureDistrictAccessClaim(districtId: string): Promise<void> {
  const user = auth?.currentUser;
  if (!user || !districtId) return;
  try {
    const result = await user.getIdTokenResult(); // 期限内なら cache (network なし)
    if (hasDistrictAccessClaim(result.claims as Record<string, unknown>, districtId)) return;
    // claim 欠落 → サーバ側で付与済の可能性。強制 refresh で最新 claim を取得する。
    await user.getIdToken(true);
  } catch {
    // token 取得/refresh 失敗は致命的でない (後続 upload が本来のエラーを出す)。
  }
}
